import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, FileText, HeartPulse, Users, BookOpen, HandHeart } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const CONFERENCES = [
  {
    title: "PEDICON 2020",
    org: "National Annual Conference, Indian Academy of Pediatrics",
    where: "Indore — January 2020",
  },
  {
    title: "30th International Pediatric Conference (IPA)",
    org: "International Pediatric Association and IAP",
    where: "Gandhinagar, Gujarat — February 2023",
  },
  {
    title: "PEDICON 2023",
    org: "National Annual Conference, IAP",
    where: "Gandhinagar, Gujarat — February 2023",
  },
  {
    title: "SPACT — Conference on Hemato-oncology",
    org: "Conference on Hemato-oncology",
    where: null,
  },
];

const WORKSHOPS = [
  { title: "Pediatric Nephrology and Peritoneal Dialysis", org: "IAP, TNMC Nair Hospital, Mumbai" },
  {
    title: "Hypoxic Ischemic Encephalopathy and Clinical & Radiological Correlation",
    org: "IAP, Wadia Children Hospital, Mumbai",
  },
  { title: "Hemato-oncology and Clinical Correlation", org: "TNMC Nair Hospital, Mumbai" },
  {
    title: "Newborn Screening & Genetic Testing for Newborn",
    org: "Grant Government Medical College & Sir JJ Group of Hospitals, Mumbai",
  },
];

const COMMUNITY = [
  "Participated as main Pediatrician in Children's Annual School Health Camp — Mahavir International School, Surat, 2022.",
  "Participated as Senior Pediatrician in Rural Health Camp for underprivileged children — Kadodara, Gujarat.",
  "NSS social service work and awareness camps.",
  "Blood donation camp organized by NKP SIMS and LMH, Nagpur.",
];

const CATEGORIES = [
  { id: "academic-card-conferences", icon: Users, title: "Conferences Attended", summary: "4 conferences", items: CONFERENCES },
  { id: "academic-card-workshops", icon: BookOpen, title: "Workshops & CME", summary: "4 workshops", items: WORKSHOPS },
  { id: "academic-card-community", icon: HandHeart, title: "Community Health", summary: "Health camps & social work", items: COMMUNITY },
];

const Badge = ({ children, plum = false }) => (
  <span
    className={`inline-block text-[11px] font-semibold rounded-full px-3.5 py-1.5 ${
      plum ? "bg-[#E8C2CB]/60 text-[#70435F]" : "bg-[#F0EAE3] text-[#695E67]"
    }`}
  >
    {children}
  </span>
);

const CategoryCard = ({ cat, open, onToggle, index }) => (
  <Reveal delay={0.07 * index} className="h-full">
    <article
      data-testid={cat.id}
      className={`bg-white border rounded-2xl transition-all duration-500 overflow-hidden ${
        open ? "border-[#E8C2CB] shadow-card" : "border-[#70435F]/10 hover:border-[#E8C2CB]"
      }`}
    >
      <button
        data-testid={`${cat.id}-toggle`}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${cat.id}-panel`}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
      >
        <span>
          <span className="block font-display text-xl font-semibold text-[#383237]">{cat.title}</span>
          <span className="block text-xs text-[#8A7E88] mt-1">{cat.summary}</span>
        </span>
        <span
          className={`shrink-0 inline-flex w-8 h-8 rounded-full border items-center justify-center transition-all duration-500 ${
            open ? "border-[#70435F] bg-[#70435F]" : "border-[#70435F]/25"
          }`}
          aria-hidden="true"
        >
          <ChevronDown
            size={16}
            className={`transition-all duration-500 ${open ? "rotate-180 text-white" : "text-[#70435F]"}`}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${cat.id}-panel`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <ul className="px-6 pb-6 pt-1 border-t border-[#F0EAE3] space-y-4">
              {cat.items.map((item, i) =>
                typeof item === "string" ? (
                  <li key={i} className="flex gap-2.5 text-sm text-[#695E67] leading-relaxed">
                    <span className="mt-[8px] w-1.5 h-1.5 rounded-full bg-[#70435F] shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ) : (
                  <li key={i} className="flex gap-2.5">
                    <span className="mt-[8px] w-1.5 h-1.5 rounded-full bg-[#70435F] shrink-0" aria-hidden="true" />
                    <span>
                      <span className="block text-sm font-semibold text-[#383237] leading-snug">{item.title}</span>
                      <span className="block text-xs text-[#8A7E88] mt-0.5">{item.org}</span>
                      {item.where && <span className="block text-xs text-[#8A7E88]">{item.where}</span>}
                    </span>
                  </li>
                )
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  </Reveal>
);

const Academic = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <section id="academic" className="py-24 sm:py-32 bg-[#FAF7F4] border-y border-[#E7DED4]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-14">
          <Eyebrow>Academic Profile</Eyebrow>
          <h2
            data-testid="academic-heading"
            className="font-display text-3xl sm:text-4xl lg:text-[42px] font-medium tracking-tight text-[#383237] leading-tight mt-5"
          >
            Research, conferences & <em className="italic text-[#70435F]">academic activities</em>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-6 mb-6 items-start">
          <Reveal className="lg:col-span-2">
            <article
              data-testid="academic-card-pedicon-2023"
              className="h-full bg-white border border-[#70435F]/10 rounded-2xl p-8 sm:p-10 hover:border-[#E8C2CB] hover:shadow-card transition-all duration-500"
            >
              <div className="flex items-start gap-5">
                <span className="shrink-0 w-12 h-12 rounded-2xl bg-[#70435F] flex items-center justify-center">
                  <FileText size={22} strokeWidth={1.5} className="text-[#F6F1EC]" />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#B89A69] mb-2.5">
                    Poster Presentation
                  </p>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#383237] leading-snug mb-5">
                    "A Study on PEFR in Healthy Urban School Children (6–17 Years) in Surat and Its
                    Correlation with Anthropometric Determinants"
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    <Badge plum>30th International Pediatric Conference & 60th PEDICON</Badge>
                    <Badge>Gandhinagar, Gujarat</Badge>
                    <Badge>February 2023</Badge>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.1}>
            <article
              data-testid="academic-card-pals"
              className="h-full bg-[#E7DED4]/50 border border-[#B89A69]/25 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <HeartPulse size={20} strokeWidth={1.5} className="text-[#70435F]" />
                <h3 className="font-display text-xl font-semibold text-[#383237]">Certifications & Courses</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-white border border-[#70435F]/10 rounded-xl px-5 py-4">
                  <p className="text-sm font-semibold text-[#383237]">Pediatric Advanced Life Support (PALS)</p>
                  <p className="text-xs text-[#8A7E88] mt-1">American Heart Association</p>
                  <p className="text-xs text-[#8A7E88]">December 2019</p>
                </div>
                <div className="bg-white border border-[#70435F]/10 rounded-xl px-5 py-4">
                  <p className="text-sm font-semibold text-[#383237]">Advanced Cardiac Life Support (ACLS)</p>
                  <p className="text-xs text-[#8A7E88] mt-1">American Heart Association</p>
                </div>
              </div>
            </article>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {CATEGORIES.map((cat, i) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              index={i}
              open={openId === cat.id}
              onToggle={() => setOpenId(openId === cat.id ? null : cat.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Academic;
